import { FaroAvatar, FaroButton, FaroIconButton, FaroTable } from '@faro/design-system';
import { ChevronDownIcon, ChevronRightIcon } from '@faro/icons';
import React, { ComponentPropsWithoutRef, ReactElement, Ref, useImperativeHandle, useRef, useState } from 'react';
import { Link, LinkProps } from 'react-router';
import useSortableTable from '../../../package/react-hooks/useSortableTable';
import { useStudySpacePermissions } from '../../../package/react-hooks/useStudySpacePermissions';
import { StudySpaceAnalyticEventKey, useStudySpaceAnalytics } from '../../../util/analytics';
import { getFormattedDateTime } from '../../../util/getFormattedDateTime';
import StudyStatusListBox from '../../StudyStatusListBox/StudyStatusListBox';
import StudyDefinitionMenu, { StudyDefinitionTableRowCallbacks } from '../StudyDefinitionMenu/StudyDefinitionMenu';
import { StudyDefinitionTableRow } from '../StudyDefinitionTable.types';
import styles from './StudyDefinitionTableRow.module.scss';

export interface StudyDefinitionRowProps extends ComponentPropsWithoutRef<'tr'>, StudyDefinitionTableRowCallbacks {
    ref?: Ref<StudyDefinitionRowRef>;
    row: StudyDefinitionTableRow;
    onRowClick: (row: StudyDefinitionTableRow) => void;
    index: number;
    onToggleExpansion?: (row: StudyDefinitionTableRow) => void;
    sortConfig?: { keys: string[]; direction: 'asc' | 'desc' };
}

export interface StudyDefinitionRowRef {
    reSortChildren: (sortKeys: string[], sortDirection: 'asc' | 'desc') => void;
}

export function StudyDefinitionRow(props: StudyDefinitionRowProps): ReactElement {
    const {
        row,
        onRowClick,
        onStatusTypeUpdate,
        onExport,
        onClone,
        onRename,
        onMove,
        onDelete,
        onCreateSubstudy,
        cloneDisabled = false,
        className = '',
        index,
        onToggleExpansion,
        sortConfig,
        ref,
        ...rest
    } = props;

    const { trackEvent } = useStudySpaceAnalytics();

    const rowRef = useRef<HTMLTableRowElement>(null);
    const studyDefinitionDescriptionRef = useRef<HTMLTableDataCellElement>(null);
    const permissions = useStudySpacePermissions();

    const [rowIsActive, setRowIsActive] = useState(false);

    // Use useSortableTable for child rows
    const { items: sortedChildRows, sortDataBy } = useSortableTable(row.childRows || [], {
        keys: sortConfig?.keys || ['updated'],
        direction: sortConfig?.direction || 'desc',
    });

    // Expose reSortChildren method to parent
    useImperativeHandle(
        ref,
        () => ({
            reSortChildren: (sortKeys: string[], sortDirection: 'asc' | 'desc') => {
                sortDataBy(sortKeys);
            },
        }),
        [sortDataBy]
    );

    const handleRowClick = () => {
        onRowClick?.(row);
    };

    const handleToggleExpansion = (event: React.MouseEvent) => {
        event.stopPropagation();
        onToggleExpansion?.(row);
    };

    const hasChildren = row.childRows && row.childRows.length > 0;
    const isExpanded = row.isExpanded ?? false;
    const isChildRow = row.parentId != null;

    const handleRowMouseDown = (event: React.MouseEvent<HTMLTableRowElement>) => {
        // Only trigger for row or study definition description
        if (event.target === rowRef.current || event.target === studyDefinitionDescriptionRef.current) {
            setRowIsActive(true);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (event.target === event.currentTarget && (event.code === 'Enter' || event.code === 'Space')) {
            setRowIsActive(true);
            onRowClick?.(row);
            event.preventDefault();
        }
    };

    return (
        <>
            <tr
                tabIndex={0}
                onKeyDown={handleKeyDown}
                onKeyUp={() => setRowIsActive(false)}
                onMouseDown={handleRowMouseDown}
                onMouseUp={() => setRowIsActive(false)}
                onMouseLeave={() => setRowIsActive(false)}
                {...rest}
                ref={rowRef}
                className={`
                    ${styles.row}
                    ${row.isDeleted ? styles.deleted : ''}
                    ${rowIsActive ? styles.activeRow : ''}
                    ${isChildRow ? styles.childRow : ''}
                    ${className}
                `}
            >
                {/* Expansion column */}
                <FaroTable.Cell className={styles.expansionCell}>
                    {hasChildren && !isChildRow && (
                        <FaroIconButton
                            className={styles.expandButton}
                            icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                            onClick={handleToggleExpansion}
                            size="small"
                        />
                    )}
                </FaroTable.Cell>

                <FaroTable.Cell
                    ref={studyDefinitionDescriptionRef}
                    data-automation-id="title"
                    className={styles.titleCell}
                    onClick={handleRowClick}
                >
                    <Link to={`/studies/${row.id}`}>
                        <div
                            className={`${styles.title} ${isChildRow ? styles.childTitle : ''}`}
                            data-testid={`study-definition-title-${index}`}
                        >
                            {row.name || 'Untitled'}
                        </div>
                    </Link>
                </FaroTable.Cell>
                <FaroTable.Cell data-automation-id="status" data-intercom-target="Change Study Definition Status">
                    {row.isDeleted ? (
                        <FaroButton
                            size="small"
                            className={`
                                ${styles.deletedButton}
                                `}
                            tabIndex={-1}
                        >
                            Deleted
                        </FaroButton>
                    ) : (
                        <StudyStatusListBox
                            readOnly={!permissions.editor && !permissions.studyDefinitionAdmin}
                            studyDefinitionId={row.id}
                            selectedStatusType={row.status.type}
                            onStatusTypeUpdate={(_, type) => onStatusTypeUpdate(row, type)}
                        />
                    )}
                </FaroTable.Cell>
                <FaroTable.Cell
                    data-automation-id="origin"
                    className={styles.originCell}
                    onClick={row.origin == null ? handleRowClick : undefined}
                >
                    {row.origin != null ? (
                        <ConditionalLink
                            shouldLink={!row.origin.isDeleted}
                            linkProps={{
                                to: `/studies/${row.origin.id}`,
                            }}
                            onClick={() => trackEvent(StudySpaceAnalyticEventKey.navigateToStudyDefinitionOriginLink)}
                            data-intercom-target="Study Definition Origin Link"
                        >
                            <div className={styles.origin}>{row.originLabel}</div>
                        </ConditionalLink>
                    ) : (
                        <></>
                    )}
                </FaroTable.Cell>
                <FaroTable.Cell className={styles.lastUpdatedCell} onClick={handleRowClick}>
                    {getFormattedDateTime(row.updated)}
                </FaroTable.Cell>
                <FaroTable.Cell onClick={handleRowClick}>
                    {row.lastUpdatedBy != null && <FaroAvatar user={row.lastUpdatedBy} />}
                </FaroTable.Cell>
                <FaroTable.Cell data-automation-id="rowControls" data-intercom-target="Study Definition Row Controls">
                    {row.isDeleted ? null : (
                        <StudyDefinitionMenu
                            studyDefinition={row}
                            onExport={onExport}
                            onClone={onClone}
                            onRename={onRename}
                            onMove={onMove}
                            onDelete={onDelete}
                            onCreateSubstudy={onCreateSubstudy}
                            cloneDisabled={cloneDisabled}
                            index={index}
                            isSubstudy={isChildRow}
                        />
                    )}
                </FaroTable.Cell>
            </tr>
            {/* Render child rows if expanded */}
            {isExpanded &&
                hasChildren &&
                sortedChildRows?.map((childRow, childIndex) => (
                    <StudyDefinitionRow
                        key={`${childRow.id}-${childIndex}`}
                        index={childIndex}
                        row={childRow}
                        onRowClick={onRowClick}
                        onStatusTypeUpdate={onStatusTypeUpdate}
                        onExport={onExport}
                        onClone={onClone}
                        onRename={onRename}
                        onMove={onMove}
                        onDelete={onDelete}
                        onCreateSubstudy={onCreateSubstudy}
                        cloneDisabled={cloneDisabled}
                        onToggleExpansion={onToggleExpansion}
                        data-automation-role="studyDefinitionRow"
                        data-intercom-target="Select Study Definition"
                        data-testid={`studyDefinitionRow-${index}-child-${childIndex}`}
                    />
                ))}
        </>
    );
}
interface ConditionalLinkProps extends ComponentPropsWithoutRef<any> {
    shouldLink: boolean;
    linkProps: LinkProps;
}
const ConditionalLink = ({ children, shouldLink, linkProps, ...rest }: ConditionalLinkProps) =>
    shouldLink ? (
        <Link {...linkProps} {...rest}>
            {children}
        </Link>
    ) : (
        <>{children}</>
    );
